import { z } from "zod";
import { ApprovalDecision, ConversationKind, Message, TaskRef } from "./entities.js";
import { ConversationId, SessionId, Usage, WakeId } from "./common.js";

/**
 * node-agent ↔ agent-runner 协议（doc/api_v1.md §4）。
 * 传输：容器内 unix socket（/run/agentsaas/runner.sock），换行分隔 JSON（NDJSON）。
 */

export const WakeReason = z.enum([
  "user_input",
  "mention",
  "mail",
  "task",
  "meeting_turn",
  "asset_ready",
  "approval_result",
]);
export type WakeReason = z.infer<typeof WakeReason>;

export const WakeBatch = z.object({
  wakeId: WakeId,
  reason: WakeReason,
  items: z.array(Message).min(1),
  context: z
    .object({
      roomDigest: z.string().optional(),
      task: TaskRef.optional(),
      /** UserId | AgentId → 显示名（渲染 [来自 @名字 · 渠道 · 时间]） */
      directory: z.record(z.string(), z.string()).optional(),
      conversations: z.record(ConversationId, z.object({ kind: ConversationKind, title: z.string().nullable() })).optional(),
    })
    .optional(),
  /** main = 长期主会话；{taskId} = 按任务隔离的子会话 */
  session: z.union([z.literal("main"), z.object({ taskId: z.string() })]).default("main"),
});
export type WakeBatch = z.infer<typeof WakeBatch>;

export const RunnerState = z.enum(["starting", "idle", "busy", "waiting_approval", "error"]);
export type RunnerState = z.infer<typeof RunnerState>;

export const RunnerEvent = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("run.started"),
    wakeId: WakeId,
    sessionId: SessionId,
    /** Claude Code 自身的会话 UUID（排障用） */
    sdkSessionId: z.string().optional(),
    /** 运行中排队的 wake 合并为一轮时，其余被合并的 wakeId */
    mergedWakeIds: z.array(WakeId).optional(),
  }),
  /** Claude Agent SDK 的 SDKMessage 原样透传；形状由 SDK 版本决定，这里不做约束 */
  z.object({ kind: z.literal("sdk.message"), wakeId: WakeId, payload: z.unknown() }),
  z.object({
    kind: z.literal("run.finished"),
    wakeId: WakeId,
    usage: Usage,
    stopReason: z.string(),
  }),
  z.object({ kind: z.literal("run.interrupted"), wakeId: WakeId }),
  z.object({ kind: z.literal("run.error"), wakeId: WakeId.nullable(), code: z.string(), detail: z.string().optional() }),
]);
export type RunnerEvent = z.infer<typeof RunnerEvent>;

/** node → runner */
export const ToRunner = z.discriminatedUnion("type", [
  z.object({ type: z.literal("wake"), batch: WakeBatch }),
  z.object({ type: z.literal("interrupt") }),
  z.object({
    type: z.literal("permission.reply"),
    requestId: z.string(),
    decision: ApprovalDecision,
    updatedInput: z.unknown().optional(),
    message: z.string().optional(),
  }),
  z.object({ type: z.literal("config.reload") }),
]);
export type ToRunner = z.infer<typeof ToRunner>;

/** runner → node */
export const FromRunner = z.discriminatedUnion("type", [
  z.object({ type: z.literal("hello"), runnerVersion: z.string(), sdkVersion: z.string() }),
  z.object({ type: z.literal("event"), event: RunnerEvent }),
  z.object({
    type: z.literal("permission.request"),
    requestId: z.string(),
    wakeId: WakeId,
    tool: z.string(),
    input: z.unknown(),
    reason: z.string().optional(),
  }),
  z.object({ type: z.literal("state"), state: RunnerState }),
  /** 权限请求作废（超时 / 中断 / SDK 取消）；控制面据此关闭审批卡。重连后未决请求会以同一 requestId 补发，需去重 */
  z.object({ type: z.literal("permission.cancel"), requestId: z.string(), reason: z.enum(["timeout", "interrupted", "aborted"]) }),
]);
export type FromRunner = z.infer<typeof FromRunner>;

export const RUNNER_SOCKET_PATH = "/run/agentsaas/runner.sock";

/** run.error 的 code：下列常量，或 `sdk.<SDK result subtype>`（如 sdk.api_error、sdk.error_max_turns）；402 复用 agent.budget_exhausted */
export const RUNNER_ERROR_CODES = ["runner.invalid_frame", "runner.model_missing", "runner.sdk_crashed", "runner.config_invalid", "runner.git_clone_failed"] as const;

/** 平台写入容器的 runner 配置文件（node lane 写入，runner 读取）。重写 .claude 时必须保留 .claude/projects/（会话 transcript） */
export const RUNNER_CONFIG_PATHS = {
  mcp: "/home/agent/.claude/mcp.json", // { mcpServers: {…} }（SDK McpServerConfig）
  runner: "/home/agent/.agentsaas/runner.json", // { model }，优先于 AGENTSAAS_MODEL；换模型后发 config.reload
  preserve: ["/home/agent/.claude/projects"],
} as const;
export const RunnerFileConfig = z.object({ model: z.string().min(1) });
